import { useEffect, useMemo, useState } from "react";
import {
  fetchTransporterEarningsSummary,
  type TransporterEarningsDailyDto,
  type TransporterEarningsSettlementDto,
  type TransporterEarningsSummaryResponse,
  type TransporterEarningsTotalsDto,
} from "../lib/fetch-transporter-delivery-ops";
import { TransporterOnboardingEarningsReadonlySummary } from "../workspace/onboarding-readonly-summary";
import { useTransporterWorkspace } from "../workspace/transporter-workspace-context";

function formatUsd(cents: number): string {
  const n = cents / 100;
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function TotalsCard({
  title,
  t,
}: {
  title: string;
  t: TransporterEarningsTotalsDto;
}) {
  return (
    <div className="td-surface p-6">
      <h2 className="text-xs font-medium uppercase tracking-wide text-td-muted">{title}</h2>
      <dl className="mt-3 space-y-2 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-td-muted">Gross delivery fees</dt>
          <dd className="tabular-nums text-td-ink">{formatUsd(t.grossTransporterFeesCents)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-td-muted">Platform fees</dt>
          <dd className="tabular-nums text-td-body">{formatUsd(t.transporterPlatformFeesCents)}</dd>
        </div>
        <div className="flex justify-between gap-4 border-t border-td-card-border pt-2">
          <dt className="text-td-ink">Net</dt>
          <dd className="tabular-nums font-medium text-success">
            {formatUsd(t.netTransporterEarningsCents)}
          </dd>
        </div>
        <div className="flex justify-between gap-4 text-xs text-td-muted">
          <dt>Completed deliveries (line items)</dt>
          <dd className="tabular-nums">{t.completedDeliveryCount}</dd>
        </div>
      </dl>
    </div>
  );
}

function DailyNetChart({ daily }: { daily: TransporterEarningsDailyDto[] }) {
  const maxNet = useMemo(
    () => daily.reduce((m, d) => Math.max(m, d.netTransporterEarningsCents), 0),
    [daily],
  );

  if (daily.length === 0) {
    return (
      <p className="text-sm text-gray">No earnings data for this date range.</p>
    );
  }

  return (
    <div className="td-surface overflow-x-auto p-4 pb-2 pt-3">
      <div className="flex min-w-min gap-1 border-b border-td-card-border">
        {daily.map((d) => {
          const pct =
            maxNet <= 0 ? 2 : Math.max(2, (d.netTransporterEarningsCents / maxNet) * 100);
          return (
            <div key={d.date} className="group flex h-40 w-3 shrink-0 flex-col justify-end sm:w-4">
              <div
                className="w-full min-h-[4px] rounded-t bg-blue/80 group-hover:bg-blue"
                style={{ height: `${pct}%` }}
                title={`${d.date}: ${formatUsd(d.netTransporterEarningsCents)} net (${d.completedDeliveryCount} stops)`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SettlementRow({ s }: { s: TransporterEarningsSettlementDto }) {
  return (
    <tr>
      <td className="text-sm font-medium text-[#333333]">{s.dispensaryLegalName}</td>
      <td className="text-xs text-[#555555]">
        {s.periodStart.slice(0, 10)} → {s.periodEnd.slice(0, 10)}
      </td>
      <td className="text-xs text-blue">{s.status}</td>
      <td className="text-sm font-medium text-td-ink tabular-nums">
        {formatUsd(s.netTransporterEarningsCents)}
      </td>
      <td className="text-center text-xs text-[#555555]">{s.successfulDeliveryCount}</td>
      <td className="text-xs text-[#555555]">
        {s.settlementCompletedAt ? s.settlementCompletedAt.slice(0, 19).replace("T", " ") : "—"}
      </td>
      <td className="text-[11px] text-[#555555]">{s.straddleTransferIdInbound ?? "—"}</td>
      <td className="text-[11px] text-[#555555]">{s.straddleTransferIdPlatformFee ?? "—"}</td>
    </tr>
  );
}

const CHART_RANGE_DAYS = [7, 30, 90, 180] as const;

export function EarningsScreen() {
  const { transporterId, getToken } = useTransporterWorkspace();
  const [data, setData] = useState<TransporterEarningsSummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chartDays, setChartDays] = useState<(typeof CHART_RANGE_DAYS)[number]>(30);
  const [settlementStatus, setSettlementStatus] = useState<string>("all");

  useEffect(() => {
    if (!transporterId) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setError(null);
    void (async () => {
      try {
        const r = await fetchTransporterEarningsSummary(getToken, transporterId, {
          dailyLookbackDays: chartDays,
        });
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) {
          setData(null);
          setError(e instanceof Error ? e.message : "earnings_load_failed");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transporterId, getToken, chartDays]);

  useEffect(() => {
    setSettlementStatus("all");
  }, [transporterId]);

  const settlementStatuses = useMemo(() => {
    if (!data?.settlements.length) return [];
    return [...new Set(data.settlements.map((s) => s.status))].sort();
  }, [data]);

  const filteredSettlements = useMemo(() => {
    if (!data) return [];
    if (settlementStatus === "all") return data.settlements;
    return data.settlements.filter((s) => s.status === settlementStatus);
  }, [data, settlementStatus]);

  if (!transporterId) {
    return (
      <section className="space-y-2">
        <p className="text-sm text-gray">
          Choose a transporter in the scope panel (platform admin) or use a linked dispatcher account.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <div>
        <p className="text-sm text-gray">
          Delivery fees and payouts for this transporter. Month-to-date follows Colorado calendar month boundaries.
        </p>
        <div className="mt-4">
          <TransporterOnboardingEarningsReadonlySummary />
        </div>
        {data ? (
          <p className="mt-1 text-xs text-gray-light">
            Figures as of{" "}
            {new Date(data.asOf).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}.
          </p>
        ) : null}
      </div>

      {error ? <p className="text-sm text-red">Could not load earnings. Try again in a moment.</p> : null}

      {data ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <TotalsCard title="Month to date" t={data.monthToDate} />
            <TotalsCard title="All time" t={data.allTime} />
          </div>

          <div>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-sm font-medium text-gray">Daily net</h2>
                <p className="mt-1 text-xs text-gray-light">
                  Each bar is one calendar day in Colorado; height reflects net earnings after platform fees.
                </p>
              </div>
              <label className="text-xs text-gray">
                Chart range
                <select
                  className="input-field ml-2 !w-auto min-w-[8rem]"
                  value={chartDays}
                  onChange={(e) => setChartDays(Number(e.target.value) as (typeof CHART_RANGE_DAYS)[number])}
                >
                  {CHART_RANGE_DAYS.map((n) => (
                    <option key={n} value={n}>
                      Last {n} days
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <DailyNetChart daily={data.daily} />
          </div>

          <div>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-sm font-medium text-gray">Recent batch statements</h2>
                <p className="mt-1 text-xs text-gray-light">Newest period end first (up to 50).</p>
              </div>
              {settlementStatuses.length > 1 ? (
                <label className="text-xs text-gray">
                  Status
                  <select
                    className="input-field ml-2 !w-auto min-w-[8rem]"
                    value={settlementStatus}
                    onChange={(e) => setSettlementStatus(e.target.value)}
                  >
                    <option value="all">All</option>
                    {settlementStatuses.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
            {data.settlements.length === 0 ? (
              <p className="mt-2 text-sm text-gray">No batch statements yet.</p>
            ) : filteredSettlements.length === 0 ? (
              <p className="mt-2 text-sm text-gray">No statements match this status filter.</p>
            ) : (
              <div className="td-table-wrap mt-3">
                <table className="td-data-table min-w-[56rem]">
                  <thead>
                    <tr>
                      <th>Dispensary</th>
                      <th>Period</th>
                      <th>Status</th>
                      <th>Net</th>
                      <th className="text-center">Deliveries</th>
                      <th>Settled</th>
                      <th>Inbound transfer</th>
                      <th>Platform fee transfer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSettlements.map((s) => <SettlementRow key={s.batchStatementId} s={s} />)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : null}

      {!data && !error ? <p className="text-sm text-gray">Loading…</p> : null}
    </section>
  );
}
